import React, { useState } from "react";
import { db } from "../../firebase";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import "./MembershipForm.css";

export default function MembershipForm() {
    const initialState = {
        firstName: "",
        lastName: "",
        dob: "",
        oib: "",
        address1: "",
        address2: "",
        zip: "",
        county: "",
        country: "",
        phone: "",
        nationality: "",
        citizenship: "",
        renewing: false,
        boardMemberBefore: false,
        profession: "",
        interests: "",
        children: "",
        businessPromo: "",
        languages: "",
        croatiaStatus: "",
        referral: "",
        agreePrivacy: false,
    };

    const [formData, setFormData] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");

    const validateField = (name, value) => {
        let error = "";
        switch (name) {
            case "phone":
                if (!/^\+?[0-9]*$/.test(value)) error = "Phone must contain only + and numbers";
                break;
            case "oib":
                if (!/^[0-9]{11}$/.test(value)) error = "OIB must be exactly 11 digits";
                break;
            case "zip":
                if (!/^[0-9]{4,5}$/.test(value)) error = "ZIP must be 4-5 digits";
                break;
            case "dob":
                if (!value) error = "Date of birth is required";
                else {
                    const dobDate = new Date(value);
                    const today = new Date();
                    if (dobDate > today) error = "Date of birth cannot be in the future";
                }
                break;
            default:
                if (typeof value === "string" && value.trim() === "") {
                    error = "This field is required";
                }
        }

        setErrors((prev) => ({ ...prev, [name]: error }));
        return error === "";
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const fieldValue = type === "checkbox" ? checked : value;
        setFormData({
            ...formData,
            [name]: fieldValue,
        });
        validateField(name, fieldValue);
    };

    const requiredFields = [
        "firstName", "lastName", "dob", "oib", "address1", "zip", "county", "country",
        "phone", "nationality", "citizenship", "profession", "interests", "children",
        "businessPromo", "languages", "croatiaStatus", "referral"
    ];

    const isValidForm =
        requiredFields.every((field) => formData[field] && !errors[field]) &&
        formData.agreePrivacy;

    const handleSubmit = async (e) => {
        e.preventDefault();

        let allValid = true;
        requiredFields.forEach((field) => {
            if (!validateField(field, formData[field])) allValid = false;
        });
        if (!allValid) return;

        setLoading(true);

        try {
            await addDoc(collection(db, "members"), {
                ...formData,
                createdAt: Timestamp.now(),
            });
            setSuccessMsg("✅ Your membership application has been submitted successfully!");
            setFormData(initialState);
            setErrors({});
        } catch (error) {
            console.error("Error adding document: ", error);
            alert("❌ Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="membership-form-container">
            <h1 className="formTitle">International Women's Club Zagreb - Membership Application</h1>
            {successMsg && <p style={{ color: "lightgreen", textAlign: "center" }}>{successMsg}</p>}
            <form className="membershipForm" onSubmit={handleSubmit}>
                <div className="form-grid">
                    <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} required />
                    <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} required />
                    <input type="date" name="dob" value={formData.dob} onChange={handleChange} required />
                    <input type="text" name="oib" placeholder="OIB" value={formData.oib} onChange={handleChange} required />
                    <input type="text" name="address1" placeholder="Address 1" value={formData.address1} onChange={handleChange} required />
                    <input type="text" name="address2" placeholder="Address 2" value={formData.address2} onChange={handleChange} />
                    <input type="text" name="zip" placeholder="ZIP Code" value={formData.zip} onChange={handleChange} required />
                    <input type="text" name="county" placeholder="County" value={formData.county} onChange={handleChange} required />
                    <input type="text" name="country" placeholder="Country" value={formData.country} onChange={handleChange} required />
                    <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required />
                    <input type="text" name="nationality" placeholder="Nationality" value={formData.nationality} onChange={handleChange} required />
                    <input type="text" name="citizenship" placeholder="Citizenship" value={formData.citizenship} onChange={handleChange} required />
                </div>

                <label>
                    <input type="checkbox" name="renewing" checked={formData.renewing} onChange={handleChange} />
                    Renewing membership?
                </label>

                <label>
                    <input type="checkbox" name="boardMemberBefore" checked={formData.boardMemberBefore} onChange={handleChange} />
                    Have you ever been a member of the board?
                </label>

                <input type="text" name="profession" placeholder="Profession" value={formData.profession} onChange={handleChange} required />
                <textarea name="interests" placeholder="Personal interests" value={formData.interests} onChange={handleChange} required />
                <textarea name="children" placeholder="Do you have children? Ages?" value={formData.children} onChange={handleChange} required />
                <textarea name="businessPromo" placeholder="Do you own a business or provide a service that you would like to promote in our Members Services Directory?" value={formData.businessPromo} onChange={handleChange} required />
                <textarea name="languages" placeholder="What languages do you speak?" value={formData.languages} onChange={handleChange} required />

                <fieldset>
                    <legend>Are you in Croatia? *</legend>
                    <label><input type="radio" name="croatiaStatus" value="Temporary" checked={formData.croatiaStatus === "Temporary"} onChange={handleChange} /> Temporary basis</label>
                    <label><input type="radio" name="croatiaStatus" value="Permanent" checked={formData.croatiaStatus === "Permanent"} onChange={handleChange} /> Permanent basis</label>
                    <label><input type="radio" name="croatiaStatus" value="Other" checked={formData.croatiaStatus === "Other"} onChange={handleChange} /> Other</label>
                </fieldset>

                <textarea name="referral" placeholder="How did you get to know the IWCZ?" value={formData.referral} onChange={handleChange} required />

                <p className="privacy-text">
                    I agree that my name and some contact information may be privately distributed among IWCZ members strictly for the club's activities and normal functioning.
                </p>
                <label>
                    <input type="checkbox" name="agreePrivacy" checked={formData.agreePrivacy} onChange={handleChange} /> I agree
                </label>

                <button type="submit" disabled={!isValidForm || loading}>
                    {loading ? "Submitting..." : "Submit Application"}
                </button>
            </form>

            <div className="declaration">
                <p>
                    <strong>DECLARATION:</strong> By submitting this application form, I hereby declare that I accept and agree with the rules of the IWCZ as established in the Statutes, a copy of which is available below.
                    I accept that photos taken at IWCZ events may be used in various IWCZ communications including newsletters, physical displays, and web pages (which include, but aren't limited to the IWCZ website and Facebook page). In such cases, your identity will not be mentioned without your consent.
                </p>
                <p>
                    You can view the IWCZ Statutes <a href="https://www.iwcz.hr/wp-content/uploads/2023/07/Statute-IWCZ-2022.pdf" target="_blank" rel="noopener noreferrer">here</a>.
                </p>
                <p>
                    The Updated Membership Fee (01.09.2025-30.06.2026) will be announced in September 2025. The annual dues cover the cost of running the IWCZ office, inclusive of rent, office expenditure etc.
                </p>
                <p>
                    Please note that our Executive Board, committee members, activities and group leaders do their jobs voluntarily without monetary compensation.
                </p>
            </div>
        </div>
    );
}